import { useState, useEffect } from "react";
import { post } from "../../utils/api";
import { toast } from "react-toastify";
import ConfirmationModal from "../../customComponents/ConfirmationModal";
import GroupModal from "./GroupModal";

const GroupSection = ({ onSaved }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await post("/cms/group/list", {});
      if (res.status) setGroups(res.data || []);
    } catch {
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await post("/cms/group/delete", { id: deleteTarget.id });
      toast.success("Category deleted");
      setDeleteTarget(null);
      fetchGroups();
      onSaved();
    } catch {
      toast.error("Delete failed");
    }
  };

  const openAdd = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = (g) => { setEditTarget(g); setModalOpen(true); };
  const handleSaved = () => { setModalOpen(false); setEditTarget(null); fetchGroups(); onSaved(); };

  return (
    <div className="bg-white border border-gray-200 rounded-xl mb-5 overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
        <div>
          <h3 className="font-semibold text-gray-900">Categories</h3>
          <p className="text-xs text-gray-400 mt-0.5">Named collections of trips with a cover photo</p>
        </div>
        <button
          onClick={openAdd}
          className="bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-gray-700 transition-colors"
        >
          + Add
        </button>
      </div>

      {/* Body */}
      <div className="px-5 py-3">
        {loading ? (
          <p className="text-sm text-gray-400 py-6 text-center">Loading...</p>
        ) : groups.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">No categories yet. Add one to get started.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-100 text-xs uppercase tracking-wide">
                <th className="pb-2 font-medium">Cover</th>
                <th className="pb-2 font-medium">Name</th>
                <th className="pb-2 font-medium">Trips</th>
                <th className="pb-2 font-medium">Status</th>
                <th className="pb-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.id} className="border-b border-gray-50 last:border-0">
                  <td className="py-3">
                    {g.cover_image_url ? (
                      <img src={g.cover_image_url} alt={g.name} className="w-9 h-9 object-cover rounded-lg" />
                    ) : (
                      <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                        <span className="material-symbols-outlined text-gray-300" style={{ fontSize: 16 }}>image</span>
                      </div>
                    )}
                  </td>
                  <td className="py-3 text-gray-800 font-medium">{g.name}</td>
                  <td className="py-3 text-gray-500 text-xs">{g.items?.length ?? 0}</td>
                  <td className="py-3">
                    <span className={`text-xs font-medium ${g.is_active ? "text-emerald-600" : "text-gray-400"}`}>
                      {g.is_active ? "● Active" : "● Inactive"}
                    </span>
                  </td>
                  <td className="py-3">
                    <div className="flex gap-1 items-center justify-end">
                      <button onClick={() => openEdit(g)}
                        className="px-2 py-1 text-blue-600 hover:text-blue-800 text-xs font-medium">
                        Edit
                      </button>
                      <button onClick={() => setDeleteTarget(g)}
                        className="px-2 py-1 text-red-400 hover:text-red-600 text-xs font-medium">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <GroupModal
          editTarget={editTarget}
          onClose={() => { setModalOpen(false); setEditTarget(null); }}
          onSaved={handleSaved}
        />
      )}

      <ConfirmationModal
        isOpen={!!deleteTarget}
        title="Delete Category"
        message={`Delete "${deleteTarget?.name}"? It will be removed from any sections it belongs to.`}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default GroupSection;
